import React from 'react'
import './Card.css'
import getCardSize from '../../../../helpers/getCardSize'
import {JacketPainter} from './JacketPainter'
import {suitsImagePromise} from '../../../../common'
import {DPI} from './Canvas'

export interface CardProps {
    ['data-key']?: string
    left?: number
    top?: number
    rank: string
    suit: string
    zIndex?: number
    width: number
    height: number
    animated?: boolean
    rotate?: number
    active?: boolean
    flip?: boolean
    z?: number
    deck: boolean
    lang: string
}

export interface CardState {
    init: boolean
}

const FONT_FAMILY = 'Arial, sans-serif'
const {width, height} = getCardSize()
const jacketPainter = new JacketPainter(width, height)

const cache: {[key: string]: ImageData} = {}

export class Card extends React.Component<CardProps, CardState> {
    public readonly key: string | undefined
    private readonly canvas: React.RefObject<HTMLCanvasElement>
    private drawTID: any = 0

    constructor(props: CardProps) {
        super(props)
        this.key = this.props['data-key']
        this.state = {init: false}
        this.canvas = React.createRef()
    }

    render(): JSX.Element {
        const id = this.props['data-key']
        const {init} = this.state
        const {rank, suit} = this.props
        const {left, top, width, height} = this.props
        const {animated, rotate, active, flip, z} = this.props
        const isFast =
            window.location.search === '?fast' && process.env.NODE_ENV && process.env.NODE_ENV === 'development'

        const className =
            'Card Card_rank' +
            rank +
            ' Card_suit' +
            suit +
            (flip ? ' Card_flip' : '') +
            (init ? ' Card_init' : '') +
            (animated ? ' Card_animated' : '') +
            (active ? ' Card_active' : '') +
            (isFast ? ' Card_withoutAnimation' : '')

        return (
            <canvas
                ref={this.canvas}
                className={className}
                data-key={id}
                style={{
                    left: (left || 0) + 'px',
                    top: (top || 10) + 'px',
                    transform: 'rotate(' + (rotate || 0) + 'deg)',
                    width: width + 'px',
                    height: height + 'px',
                    zIndex: z,
                }}
            />
        )
    }

    componentDidMount(): void {
        this.draw()
        setTimeout(() => this.setState({init: true}), 100)
    }

    componentDidUpdate(prevProps: Readonly<CardProps>, prevState: Readonly<CardState>, snapshot?: any): void {
        const {flip, deck} = this.props

        if (prevProps.flip !== flip) {
            if (this.drawTID) {
                clearTimeout(this.drawTID)
            }
            if (!flip && deck) {
                this.drawTID = setTimeout(() => this.draw(), 400)
            } else {
                this.draw()
            }
        }
    }

    private draw(): void {
        const canvas = this.canvas.current

        if (canvas) {
            const {width, height} = getCardSize()
            const {rank, suit} = this.props
            canvas.width = width * DPI
            canvas.height = height * DPI
            canvas.style.width = width + 'px'
            canvas.style.height = height + 'px'
            const ctx = canvas.getContext('2d', {
                alpha: false,
            }) as CanvasRenderingContext2D

            if (ctx) {
                ctx.scale(DPI, DPI)
                ctx.fillStyle = '#f8f8f8'
                ctx.fillRect(0, 0, width, height)

                if (this.props.flip) {
                    suitsImagePromise.then(img => {
                        if (!this.props.flip) {
                            return
                        }
                        let key = rank + suit + width + height

                        if (!cache[key]) {
                            this.drawFace(ctx, width, height, img)
                            cache[key] = ctx.getImageData(0, 0, width * DPI, height * DPI)
                            return
                        }
                        ctx.putImageData(cache[key], 0, 0)
                    })
                } else {
                    Card.drawJacket(ctx, width, height)
                }
            }
        }
    }

    private drawFace(ctx: CanvasRenderingContext2D, width: number, height: number, img: HTMLImageElement): void {
        const {rank, suit} = this.props
        const fontSize = Card.fontSize()
        ctx.fillStyle = Card.textColor(suit)
        this.drawName(ctx, img)
        ctx.save()
        const cx = Math.round(width / 2)
        const cy = Math.round(height / 2)
        ctx.translate(cx, cy)
        ctx.rotate((180 * Math.PI) / 180)
        ctx.translate(-cx, -cy)
        this.drawName(ctx, img)
        ctx.restore()

        const rankName = Card.rankName(rank, this.props.lang)

        if (rankName) {
            ctx.font = fontSize + 'px ' + FONT_FAMILY
            const textMetrics = ctx.measureText(rankName)
            const left = Math.round((width - textMetrics.width) / 2)
            const top = Math.round(cy + fontSize / 2)
            ctx.fillText(rankName, left, top)
        }
    }

    private drawName(ctx: CanvasRenderingContext2D, img: HTMLImageElement) {
        const {rank, suit} = this.props
        const fontSize = Card.fontSize()
        const textY = fontSize + 2
        ctx.font = fontSize + 'px ' + FONT_FAMILY
        // Старый способ
        // ctx.fillText(rank + suitSymbol(suit), 10, textY)
        ctx.fillText(rank, 10, textY)
        const textMetrics = ctx.measureText(rank)
        let pos = [suit === 'S' || suit === 'D' ? 0 : 100, suit === 'S' || suit === 'H' ? 0 : 100, 100, 100]
        let w = fontSize - 5
        let x = textMetrics.width + 10
        let y = fontSize < 24 ? fontSize * 0.4 : fontSize * 0.3
        // @ts-ignore
        let args = [img].concat(pos).concat(x, y, w, w)
        ctx.drawImage.apply(ctx, args)
    }

    private static drawJacket(ctx: CanvasRenderingContext2D, width: number, height: number): void {
        const jacket = jacketPainter.getCanvas()
        ctx.drawImage(jacket, 0, 0, jacket.width, jacket.height, 0, 0, width, height)
    }

    private static fontSize(): number {
        const windowSize = Math.min(window.innerWidth, window.innerHeight)
        let fontSize = 36

        if (windowSize < 650) {
            fontSize = 24
        }
        if (windowSize < 450) {
            fontSize = 20
        }
        if (windowSize < 400) {
            fontSize = 18
        }
        return fontSize
    }

    private static textColor(suit: string): string {
        if (suit === 'H' || suit === 'D') return '#ee374d'
        return '#36343f'
    }

    private static rankName(rank: string, lang: string): string | void {
        const ru = lang === 'ru'
        if (rank === 'J') return ru ? 'Валет' : 'Jack'
        if (rank === 'Q') return ru ? 'Дама' : 'Queen'
        if (rank === 'K') return ru ? 'Король' : 'King'
        if (rank === 'A') return ru ? 'Туз' : 'Ace'
    }
}
